import { FC, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Save, Close } from '@mui/icons-material';
import { Fade, Button, ButtonGroup, Box, TextField, Grid } from '@mui/material';
import { Formik, Field, FieldProps, Form } from 'formik';
import { object, string } from 'yup';
// Components
import { Modal, Loader } from '../../../../components';
// Fetch, Helpers & Models
import { addExternalAgent, getClientAgents } from '../../../../fetch';
import { IExternalAgent, IClientAgent } from '../../../../models';
import { formatInputPhoneNumber, yupPhoneValidation } from '../../../../helpers';

interface IAddExternalAgentModal {
  open: boolean;
  onClose: () => void;
  handleSave: (newClientId: number) => Promise<void>;
  agentId: number;
}

const initialValues: IExternalAgent = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: ''
};

const validationSchema = object({
  firstName: string().required('Required').max(255, 'Max 255 characters'),
  lastName: string().required('Required').max(255, 'Max 255 characters'),
  email: string().email('Invalid email').required('Required'),
  phone: yupPhoneValidation(),
  company: string().max(255, 'Max 255 characters').nullable(),
});

export const AddExternalAgentModal: FC<IAddExternalAgentModal> = ({
  open,
  onClose,
  handleSave,
  agentId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [existingAgents, setExistingAgents] = useState<IClientAgent[]>([]);

  useEffect(() => {
    const fetchExistingAgents = async () => {
      try {
        const data = await getClientAgents();
        setExistingAgents(data);
      } catch (error) {
        enqueueSnackbar('Failed to fetch agents', { variant: 'error' });
      }
    };

    if (open) {
      fetchExistingAgents();
    }
  }, [enqueueSnackbar, open]);

  const isDuplicateAgent = (agentId: number) => {
    return existingAgents.some(agent => agent.externalAgentId === agentId);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} maxWidth="sm" title="Add Agent">
      <Fade in={open}>
        <Box marginTop={'1.25rem'}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, actions) => {
              if (isDuplicateAgent(agentId)) {
                enqueueSnackbar('This agent already exists.', { variant: 'error' });
                return;
              }
              try {
                const newPersonId = await addExternalAgent(values);
                actions.resetForm();
                handleSave(newPersonId);
                handleClose();
                enqueueSnackbar('New Agent added successfully!', { variant: 'success' });
              } catch (error) {
                const errorMessage =
                  error?.Detail || 'Something went wrong. External agent was not created.';
                enqueueSnackbar(errorMessage, { variant: 'error' });
              }
            }}
          >
            {({ isSubmitting, handleSubmit, dirty, isValid, setFieldValue }) => (
              <Form onSubmit={handleSubmit} autoComplete="off">
                {isSubmitting && <Loader type="fullscreen" position="centered" />}
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Field name="firstName">
                      {({ field, form }: FieldProps<IExternalAgent>) => (
                        <TextField
                          {...field}
                          error={form.touched.firstName && !!form.errors.firstName}
                          helperText={form.touched.firstName && form.errors.firstName}
                          fullWidth
                          required
                          label="First Name"
                          size="small"
                          autoComplete="nope"
                          onChange={e => setFieldValue('firstName', e.target.value)}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="lastName">
                      {({ field, form }: FieldProps<IExternalAgent>) => (
                        <TextField
                          {...field}
                          error={form.touched.lastName && !!form.errors.lastName}
                          helperText={form.touched.lastName && form.errors.lastName}
                          fullWidth
                          required
                          label="Last Name"
                          size="small"
                          autoComplete="nope"
                          onChange={e => setFieldValue('lastName', e.target.value)}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="email">
                      {({ field, form }: FieldProps<IExternalAgent>) => (
                        <TextField
                          {...field}
                          error={form.touched.email && !!form.errors.email}
                          helperText={form.touched.email && form.errors.email}
                          fullWidth
                          required
                          label="Email"
                          size="small"
                          autoComplete="nope"
                          type="email"
                          onChange={e => setFieldValue('email', e.target.value)}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="phone">
                      {({ field, form }: FieldProps<IExternalAgent>) => (
                        <TextField
                          {...field}
                          error={form.touched.phone && !!form.errors.phone}
                          helperText={form.touched.phone && form.errors.phone}
                          fullWidth
                          label="Phone"
                          size="small"
                          autoComplete="nope"
                          onChange={e => setFieldValue('phone', formatInputPhoneNumber(e.target.value)) }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="company">
                      {({ field, form }: FieldProps<IExternalAgent>) => (
                        <TextField
                          {...field}
                          error={form.touched.company && !!form.errors.company}
                          helperText={form.touched.company && form.errors.company}
                          fullWidth
                          label="Company"
                          size="small"
                          autoComplete="nope"
                          onChange={e => setFieldValue('company', e.target.value)}
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
                <Box mt={1}>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || !dirty || !isValid}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      disabled={isSubmitting}
                      startIcon={<Close />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Fade>
    </Modal>
  );
};
